import type { MarketDataSourceOption } from "../types/marketDataSources";
import type { MarketDataCandidateBroker, ResolveInstrumentResponse } from "../types/instruments";
import type { AvailableInstrumentRow } from "../types/tradeManagementReport";
import { resolveSourcePickerCaveat } from "./marketDataAttributionCopy";

export type InstrumentEditorListingRow = Pick<
  AvailableInstrumentRow,
  | "market_data_sources"
  | "candidate_brokers"
  | "md_listing_key"
  | "market_data_broker"
  | "market_data_broker_display"
  | "market_data_source_label"
  | "selected_market_data_source"
  | "ibkr_conid"
  | "ibkr_exchange"
>;

/** OHLC vendor menu — market_data_sources[] (+ candidate_brokers merge) only; never synthesize from lane fields. */
export function ohlcSourceMenuEntries(input: {
  market_data_sources?: MarketDataSourceOption[];
  candidate_brokers?: MarketDataCandidateBroker[];
}): MarketDataSourceOption[] {
  return resolveMarketDataSourceOptions(input);
}

/** True when /resolve returned lane metadata but an empty source menu — retry with market_data_symbol. */
export function resolveNeedsOhlcSourceMenuRetry(
  result: Pick<
    ResolveInstrumentResponse,
    | "market_data_sources"
    | "candidate_brokers"
    | "market_data_symbol"
    | "md_listing_key"
    | "market_data_broker"
  > | null | undefined
): boolean {
  if (!result) return false;
  if (ohlcSourceMenuEntries(result).length > 0) return false;
  return Boolean(
    result.market_data_symbol?.trim() ||
      result.md_listing_key?.trim() ||
      result.market_data_broker?.trim()
  );
}

/** Prefer resolve payload for OHLC menu; never synthesize a single option from md_listing_key alone. */
export function marketDataSourcesForInstrumentEditor(
  resolveResult: ResolveInstrumentResponse | null | undefined,
  row: InstrumentEditorListingRow
): MarketDataSourceOption[] {
  if (resolveResult) {
    return ohlcSourceMenuEntries({
      market_data_sources: resolveResult.market_data_sources,
      candidate_brokers: resolveResult.candidate_brokers,
    });
  }

  return ohlcSourceMenuEntries({
    market_data_sources: row.market_data_sources,
    candidate_brokers: row.candidate_brokers,
  });
}

export function marketDataBrokerKey(source: MarketDataSourceOption): string {
  return (source.market_data_broker ?? source.broker ?? "").trim().toLowerCase();
}

export function isIbkrMarketDataBroker(broker?: string): boolean {
  return (broker ?? "").trim().toLowerCase() === "ibkr";
}

export function marketDataSourcesFromCandidates(
  candidates?: MarketDataCandidateBroker[]
): MarketDataSourceOption[] {
  if (!candidates?.length) return [];
  return candidates.map((c) => ({
    broker: c.broker,
    market_data_broker: c.broker,
    broker_display: c.broker_display,
    market_data_broker_display: c.broker_display,
    md_listing_key: c.md_listing_key,
    reason: c.reason,
    confidence: c.confidence,
  }));
}

export function resolveMarketDataSourceOptions(input: {
  market_data_sources?: MarketDataSourceOption[];
  candidate_brokers?: MarketDataCandidateBroker[];
}): MarketDataSourceOption[] {
  const fromSources = input.market_data_sources?.filter(Boolean) ?? [];
  const fromCandidates = marketDataSourcesFromCandidates(input.candidate_brokers);
  if (fromSources.length === 0) return fromCandidates;
  if (fromCandidates.length === 0) return fromSources;

  const merged = [...fromSources];
  for (const candidate of fromCandidates) {
    if (indexOfMatchingMarketDataSource(merged, candidate) < 0) {
      merged.push(candidate);
    }
  }
  return merged;
}

export function isMarketDataSourceOptionAvailable(
  source: MarketDataSourceOption | null | undefined
): boolean {
  if (!source) return true;
  return source.available !== false;
}

export function formatMarketDataSourceUnavailableReason(
  reason: string | null | undefined
): string | null {
  const normalized = (reason ?? "").trim().toLowerCase();
  if (!normalized) return null;
  if (normalized === "broker_not_connected") {
    return "Reconnect this broker under Broker Management to use this OHLC source.";
  }
  if (normalized === "market_data_consent_required") {
    return "Grant market data consent for this broker feed before confirming.";
  }
  return normalized.replace(/_/g, " ");
}

export function formatMarketDataSourceOptionMenuLabel(source: MarketDataSourceOption): string {
  const base = formatMarketDataSourceOptionLabel(source);
  if (source.available === false) {
    const reason = (source.unavailable_reason ?? "").trim().toLowerCase();
    if (reason === "broker_not_connected") return `${base} (not connected)`;
    if (reason === "market_data_consent_required") return `${base} (consent required)`;
    return `${base} (unavailable)`;
  }
  return base;
}

export function resolveMarketDataSourcePickerSubtitle(
  source: MarketDataSourceOption | null | undefined
): string | null {
  if (!source) return null;
  if (source.available === false) {
    return formatMarketDataSourceUnavailableReason(source.unavailable_reason);
  }
  return resolveSourcePickerCaveat(source) ?? null;
}

/** True when the user saved an OHLC listing via market-data-preference (not just a BE default). */
export function hasSavedMarketDataSourcePreference(input: {
  saved_market_data_broker?: string | null;
  saved_ibkr_conid?: number | null;
  saved_ibkr_exchange?: string | null;
}): boolean {
  if (input.saved_market_data_broker?.trim()) return true;
  if (input.saved_ibkr_conid != null) return true;
  if (input.saved_ibkr_exchange?.trim()) return true;
  return false;
}

type MarketDataSourceChoiceInput = {
  selected_market_data_source?: MarketDataSourceOption | null;
  md_listing_key?: string | null;
  resolved?: boolean;
  requires_market_data_source_choice?: boolean;
  market_data_sources?: MarketDataSourceOption[];
  candidate_brokers?: MarketDataCandidateBroker[];
  saved_market_data_broker?: string | null;
  saved_ibkr_conid?: number | null;
  saved_ibkr_exchange?: string | null;
};

/** User or BE has a committed OHLC listing (saved preference or single unambiguous source). */
export function hasEffectiveMarketDataSourceSelection(
  input: MarketDataSourceChoiceInput
): boolean {
  if (input.requires_market_data_source_choice) {
    return hasSavedMarketDataSourcePreference(input);
  }
  if (input.selected_market_data_source) return true;
  const sources = resolveMarketDataSourceOptions(input);
  if (sources.length > 1) return false;
  if (input.resolved === true && input.md_listing_key?.trim()) return true;
  return false;
}

export function needsMarketDataSourceChoice(input: MarketDataSourceChoiceInput): boolean {
  if (hasEffectiveMarketDataSourceSelection(input)) return false;
  const sources = resolveMarketDataSourceOptions(input);
  if (input.requires_market_data_source_choice && sources.length > 0) return true;
  return sources.length > 1;
}

export function needsMarketDataSourceChoiceForRow(row: AvailableInstrumentRow): boolean {
  return needsMarketDataSourceChoice({
    requires_market_data_source_choice: row.requires_market_data_source_choice,
    selected_market_data_source: row.selected_market_data_source,
    md_listing_key: row.md_listing_key,
    resolved: row.resolved,
    market_data_sources: row.market_data_sources,
    candidate_brokers: row.candidate_brokers,
    saved_market_data_broker: row.saved_market_data_broker,
    saved_ibkr_conid: row.saved_ibkr_conid,
    saved_ibkr_exchange: row.saved_ibkr_exchange,
  });
}

export function marketDataSourceChoiceInputFromResolve(
  result: Pick<
    ResolveInstrumentResponse,
    | "requires_market_data_source_choice"
    | "selected_market_data_source"
    | "md_listing_key"
    | "resolved"
    | "market_data_sources"
    | "candidate_brokers"
    | "saved_market_data_broker"
    | "saved_ibkr_conid"
    | "saved_ibkr_exchange"
  >
): MarketDataSourceChoiceInput {
  return {
    requires_market_data_source_choice: result.requires_market_data_source_choice,
    selected_market_data_source: result.selected_market_data_source,
    md_listing_key: result.md_listing_key,
    resolved: result.resolved,
    market_data_sources: result.market_data_sources,
    candidate_brokers: result.candidate_brokers,
    saved_market_data_broker: result.saved_market_data_broker,
    saved_ibkr_conid: result.saved_ibkr_conid,
    saved_ibkr_exchange: result.saved_ibkr_exchange,
  };
}

export function needsMarketDataSourceChoiceForResolve(result: ResolveInstrumentResponse): boolean {
  return needsMarketDataSourceChoice(marketDataSourceChoiceInputFromResolve(result));
}

export function marketDataSourceOptionsForRow(
  row: AvailableInstrumentRow | undefined
): MarketDataSourceOption[] {
  if (!row) return [];
  return resolveMarketDataSourceOptions({
    market_data_sources: row.market_data_sources,
    candidate_brokers: row.candidate_brokers,
  });
}

export function formatMarketDataSourceSelectLabel(source: MarketDataSourceOption): string {
  return joinUniqueMarketDataLabelParts([
    formatMarketDataSourceOptionLabel(source),
    formatMarketDataSourceOptionDescription(source),
  ]);
}

export function defaultMarketDataSourceOption(
  sources: MarketDataSourceOption[]
): MarketDataSourceOption | undefined {
  if (!sources.length) return undefined;
  const preferred =
    sources.find((s) => s.is_selected) ??
    sources.find((s) => s.is_default) ??
    sources[0];
  // IBKR often includes a venue parent row (no conid) plus month contracts. Prefer a
  // concrete contract so Confirm does not target the exchange-only row.
  if (
    preferred &&
    isIbkrMarketDataBroker(marketDataBrokerKey(preferred)) &&
    (preferred.ibkr_conid == null || preferred.ibkr_conid === undefined)
  ) {
    const venue = preferred.venue ?? preferred.venue_label;
    const sameVenueWithConid = sources.find(
      (s) =>
        isIbkrMarketDataBroker(marketDataBrokerKey(s)) &&
        s.ibkr_conid != null &&
        (s.venue ?? s.venue_label) === venue
    );
    if (sameVenueWithConid) return sameVenueWithConid;
    const anyWithConid = sources.find(
      (s) => isIbkrMarketDataBroker(marketDataBrokerKey(s)) && s.ibkr_conid != null
    );
    if (anyWithConid) return anyWithConid;
  }
  return preferred;
}

/** Exact match index for a user-selected listing, or -1 (does not fall back to default). */
export function indexOfMatchingMarketDataSource(
  sources: MarketDataSourceOption[],
  selected?: MarketDataSourceOption | null
): number {
  if (!sources.length || !selected) return -1;

  const byOptionId = sources.findIndex(
    (source) =>
      source.option_id && selected.option_id && source.option_id === selected.option_id
  );
  if (byOptionId >= 0) return byOptionId;

  const byListingKey = sources.findIndex(
    (source) =>
      source.md_listing_key &&
      selected.md_listing_key &&
      source.md_listing_key === selected.md_listing_key
  );
  if (byListingKey >= 0) return byListingKey;

  const byBrokerVenueConid = sources.findIndex(
    (source) =>
      (source.market_data_broker ?? source.broker) ===
        (selected.market_data_broker ?? selected.broker) &&
      (source.venue ?? source.venue_label) === (selected.venue ?? selected.venue_label) &&
      source.ibkr_conid != null &&
      selected.ibkr_conid != null &&
      Number(source.ibkr_conid) === Number(selected.ibkr_conid)
  );
  if (byBrokerVenueConid >= 0) return byBrokerVenueConid;

  const brokerVenueMatches = sources
    .map((source, index) => ({ source, index }))
    .filter(
      ({ source }) =>
        (source.market_data_broker ?? source.broker) ===
          (selected.market_data_broker ?? selected.broker) &&
        (source.venue ?? source.venue_label) === (selected.venue ?? selected.venue_label)
    );
  if (brokerVenueMatches.length > 0) {
    const withConid = brokerVenueMatches.find(({ source }) => source.ibkr_conid != null);
    if (withConid) return withConid.index;
    return brokerVenueMatches[0].index;
  }

  // Binance spot vs futures: match product lane even when listing keys differ.
  const selectedProduct = inferBinanceProductLane(selected);
  if (selectedProduct && marketDataBrokerKey(selected) === "binance") {
    const byProduct = sources.findIndex(
      (source) =>
        marketDataBrokerKey(source) === "binance" &&
        inferBinanceProductLane(source) === selectedProduct
    );
    if (byProduct >= 0) return byProduct;
  }

  return -1;
}

/** Infer Binance spot/futures from explicit fields, venue labels, or listing keys. */
export function inferBinanceProductLane(
  source: Pick<
    MarketDataSourceOption,
    "binance_product" | "venue" | "venue_label" | "md_listing_key" | "option_id"
  >
): "spot" | "futures" | undefined {
  const candidates = [
    source.binance_product,
    source.venue,
    source.venue_label,
    source.md_listing_key,
    source.option_id,
  ];
  for (const candidate of candidates) {
    const normalized = String(candidate || "")
      .trim()
      .toLowerCase();
    if (!normalized) continue;
    if (normalized === "futures" || normalized.includes(":futures:") || normalized.includes("futures")) {
      return "futures";
    }
    if (
      normalized.includes("usd-m") ||
      normalized.includes("usdⓈ-m") ||
      normalized.includes("perpetual")
    ) {
      return "futures";
    }
    if (normalized === "spot" || normalized.includes(":spot:") || normalized.includes("spot")) {
      return "spot";
    }
  }
  return undefined;
}

/** Index into `sources` for the BE-selected or default listing (report wizard parity). */
export function findMarketDataSourceIndex(
  sources: MarketDataSourceOption[],
  selected?: MarketDataSourceOption | null
): number {
  if (!sources.length) return 0;
  const matched = indexOfMatchingMarketDataSource(sources, selected);
  if (matched >= 0) return matched;
  const defaultSource = defaultMarketDataSourceOption(sources);
  if (!defaultSource) return 0;
  const defaultIndex = sources.indexOf(defaultSource);
  return defaultIndex >= 0 ? defaultIndex : 0;
}

/** Strip BE/internal listing keys from user-facing labels, e.g. `OHLC via OANDA (oanda:XAU_USD)`. */
export function stripInternalMarketDataLabelSuffix(label: string): string {
  return label.replace(/\s*\([^)]*:[^)]+\)\s*$/, "").trim();
}

function userFacingMarketDataSourceLabel(label: string | undefined): string | undefined {
  const trimmed = label?.trim();
  if (!trimmed) return undefined;
  return stripInternalMarketDataLabelSuffix(trimmed);
}

/** Never show raw broker slugs (liquidbrokers, twelvedata) in UI when display names exist. */
export function humanizeMarketDataBrokerSlug(slug: string | undefined): string | undefined {
  const normalized = (slug || "").trim().toLowerCase();
  if (!normalized) return undefined;
  const known: Record<string, string> = {
    liquidbrokers: "Liquid Charts",
    liquid: "Liquid Brokers",
    twelvedata: "Twelve Data",
    coinapi: "CoinAPI",
    hyperliquid: "Hyperliquid",
    databento: "US market data",
    ctrader: "cTrader",
    oanda: "OANDA",
    binance: "Binance",
    ibkr: "Interactive Brokers",
  };
  return known[normalized];
}

function marketDataProviderDisplayName(source: MarketDataSourceOption): string {
  return (
    source.market_data_broker_display?.trim() ||
    source.broker_display?.trim() ||
    humanizeMarketDataBrokerSlug(source.market_data_broker ?? source.broker) ||
    "Market data"
  );
}

/** Crypto / composite Twelve Data lanes have no exchange venue — only "Twelve Data". */
export function isGenericTwelveDataListingVenue(
  venue: string | undefined,
  providerDisplay: string
): boolean {
  const normalizedVenue = venue?.trim().toLowerCase();
  if (!normalizedVenue) return true;
  if (normalizedVenue === "twelve data") return true;
  if (normalizedVenue === providerDisplay.trim().toLowerCase()) return true;
  return false;
}

function resolvedListingVenueForDisplay(
  source: MarketDataSourceOption,
  provider: string
): string | undefined {
  const venue = (source.venue_label ?? source.venue)?.trim();
  if (!venue) return undefined;
  const broker = marketDataBrokerKey(source);
  if (broker === "twelvedata" && isGenericTwelveDataListingVenue(venue, provider)) {
    return undefined;
  }
  if (venue.toLowerCase() === provider.toLowerCase()) return undefined;
  return venue;
}

export function formatMarketDataSourceOptionLabel(source: MarketDataSourceOption): string {
  const broker = marketDataBrokerKey(source);
  const provider = marketDataProviderDisplayName(source);
  const listingVenue = resolvedListingVenueForDisplay(source, provider);

  // CoinAPI + Twelve Data equities: listing venue is the meaningful choice.
  if ((broker === "coinapi" || broker === "twelvedata") && listingVenue) {
    return `${listingVenue} (${provider})`;
  }

  if (listingVenue) {
    return `${provider} · ${listingVenue}`;
  }

  const product = source.binance_product?.trim().toLowerCase();
  if (product === "spot" || product === "futures") {
    return `${provider} · ${product === "futures" ? "Futures" : "Spot"}`;
  }

  const listingKey = String(source.md_listing_key || source.option_id || "");
  const binanceKind = /^binance:(spot|futures)\b/i.exec(listingKey)?.[1]?.toLowerCase();
  if (binanceKind) {
    return `${provider} · ${binanceKind === "futures" ? "Futures" : "Spot"}`;
  }

  if (broker === "twelvedata" && !listingVenue) {
    return `${provider} (aggregated)`;
  }

  const preset = userFacingMarketDataSourceLabel(source.market_data_source_label);
  if (preset) return preset;

  return provider;
}

/** Case-insensitive + whitespace-normalized equality for deduping label parts. */
function normalizeLabelPart(text: string): string {
  return text.trim().replace(/\s+/g, " ").toLowerCase();
}

/**
 * Join label parts with " — ", dropping duplicates and parts already covered by earlier text.
 * Keeps first occurrence order.
 */
export function joinUniqueMarketDataLabelParts(parts: Array<string | null | undefined>): string {
  const out: string[] = [];

  for (const raw of parts) {
    const part = raw?.trim();
    if (!part) continue;
    const key = normalizeLabelPart(part);
    if (!key) continue;
    if (
      out.some((existing) => {
        const existingKey = normalizeLabelPart(existing);
        return existingKey === key || existingKey.includes(key) || key.includes(existingKey);
      })
    ) {
      continue;
    }
    out.push(part);
  }

  return out.join(" — ");
}

export function formatMarketDataSourceOptionDescription(
  source: MarketDataSourceOption
): string | undefined {
  const joined = joinUniqueMarketDataLabelParts([source.description, source.reason]);
  return joined || undefined;
}

export function formatAvailableInstrumentTradeLabel(row: AvailableInstrumentRow): string {
  const tradeLabel = `${row.trade_count} trade${row.trade_count === 1 ? "" : "s"}`;
  const long = row.long_count ?? row.long_trades ?? 0;
  const short = row.short_count ?? row.short_trades ?? 0;
  if (
    typeof row.long_count === "number" ||
    typeof row.short_count === "number" ||
    typeof row.long_trades === "number" ||
    typeof row.short_trades === "number"
  ) {
    return `${tradeLabel} · ${long} long · ${short} short`;
  }
  return tradeLabel;
}

export function formatAvailableInstrumentOptionLabel(row: AvailableInstrumentRow): string {
  const tradePart = formatAvailableInstrumentTradeLabel(row);
  const mdLabel = userFacingMarketDataSourceLabel(row.market_data_source_label);
  if (mdLabel) return `${row.symbol} · ${tradePart} — ${mdLabel}`;
  const brokerDisplay = row.market_data_broker_display?.trim();
  if (brokerDisplay) return `${row.symbol} · ${tradePart} — OHLC via ${brokerDisplay}`;
  return `${row.symbol} · ${tradePart}`;
}

export function selectedInstrumentOhlcSubline(
  row: AvailableInstrumentRow | undefined
): string | null {
  if (!row) return null;
  const label = userFacingMarketDataSourceLabel(row.market_data_source_label);
  if (label) return label;
  const broker = row.market_data_broker_display?.trim();
  if (broker) return `OHLC via ${broker}`;
  return null;
}
